import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  TrendingUp, CheckCircle, ArrowLeft, Award, Clock,
  ArrowRight, Building2, Heart, GraduationCap, Briefcase, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { getAuthToken } from '@/utils/secureStorage';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const stages = [
  {
    id: 'Stage_1',
    name: 'Foundation',
    duration: '2-4 weeks',
    badge: 'Foundation Ready',
    description: 'Build soft skills, create your professional profile, explore career options.',
    tasks: [
      'Complete soft skills workshops',
      'Build resume and LinkedIn profile',
      'Attend industry orientation sessions',
      'Meet with career coach'
    ],
    icon: GraduationCap,
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'Stage_2',
    name: 'Skill Build',
    duration: '4-12 weeks',
    badge: 'Sector Certified',
    description: 'Gain sector-specific training, earn credentials, prepare for employers.',
    tasks: [
      'Complete sector training program',
      'Earn industry certification',
      'Practice mock interviews',
      'Visit employer worksites'
    ],
    icon: Award,
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 'Stage_3',
    name: 'Launch',
    duration: '2-4 weeks+',
    badge: 'Employed',
    description: 'Active job search, employer referrals, placement and post-hire support.',
    tasks: [
      'Develop job search strategy',
      'Receive employer referrals',
      'Interview with partner employers',
      'Negotiate and accept offer'
    ],
    icon: Briefcase,
    color: 'from-purple-500 to-violet-600'
  },
  {
    id: 'Employed',
    name: 'Thriving',
    duration: 'Ongoing',
    badge: '90-Day Retained',
    description: 'Post-hire check-ins, continued support, career advancement.',
    tasks: [
      '30-day check-in',
      '90-day check-in',
      'Track wage progression',
      'Connect to advancement opportunities'
    ],
    icon: Star,
    color: 'from-amber-500 to-orange-600'
  }
];

export default function PathwayDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [participant, setParticipant] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const token = getAuthToken();
      const [participantRes, sectorsRes] = await Promise.all([
        fetch(`${API}/api/workforce/participant`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/workforce/sectors`)
      ]);

      if (participantRes.ok) {
        const data = await participantRes.json();
        if (data.enrolled) {
          setParticipant(data.profile);
        }
      }
      if (sectorsRes.ok) {
        setSectors((await sectorsRes.json()).sectors);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdvanceStage = async (newStage) => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API}/api/workforce/participant/pathway`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stage: newStage })
      });

      if (res.ok) {
        toast.success(`Advanced to ${newStage.replace('_', ' ')}!`);
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to update stage');
    }
  };

  const getCurrentStageIndex = () => {
    if (!participant) return -1;
    return stages.findIndex(s => s.id === participant.pathway.current_stage);
  };

  const getSectorName = (sectorId) => {
    return sectors.find(s => s.id === sectorId)?.name || 'Not assigned';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-green-50/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <TrendingUp className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Log In to View Your Pathway</h2>
            <p className="text-slate-600 mb-6">Track your progress through the workforce development program.</p>
            <Link to="/login">
              <Button className="bg-green-600 hover:bg-green-700">Log In</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!participant) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-green-50/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <TrendingUp className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Start Your Journey</h2>
            <p className="text-slate-600 mb-6">Complete the needs assessment to create your personalized pathway.</p>
            <Link to="/workforce/assessment">
              <Button className="bg-green-600 hover:bg-green-700">
                Start Assessment <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentIndex = getCurrentStageIndex();
  const progressPercentage = ((currentIndex + 1) / stages.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-green-50/30" data-testid="pathway-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/workforce" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Workforce Hub
          </Link>
          
          <div className="flex items-start gap-4">
            <div className="p-4 bg-white/20 rounded-xl">
              <TrendingUp className="h-10 w-10" />
            </div>
            <div>
              <Badge className="mb-2 bg-white/20 text-white border-0">Your Pathway</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                {stages[currentIndex]?.name || 'Getting Started'}
              </h1>
              <p className="text-green-100">
                Sector: {getSectorName(participant.intake.assigned_sector)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Overall Progress */}
        <Card className="mb-8">
          <CardContent className="py-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-slate-700">Overall Progress</span>
              <span className="text-2xl font-bold text-green-600">{Math.round(progressPercentage)}%</span>
            </div>
            <Progress value={progressPercentage} className="h-4" />
            
            {/* Badges Earned */}
            {participant.proof_badges_earned?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500 mb-2">PROOF Badges Earned</p>
                <div className="flex flex-wrap gap-2">
                  {participant.proof_badges_earned.map(badge => (
                    <Badge key={badge} className="bg-green-100 text-green-700">
                      <Award className="h-3 w-3 mr-1" /> {badge}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stage Cards */}
        <div className="space-y-6">
          {stages.map((stage, idx) => {
            const isComplete = idx < currentIndex;
            const isCurrent = idx === currentIndex;
            const isLocked = idx > currentIndex;
            const StageIcon = stage.icon;

            return (
              <motion.div
                key={stage.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className={`overflow-hidden ${
                  isCurrent ? 'border-2 border-green-400 shadow-lg' : 
                  isComplete ? 'border-green-200 bg-green-50/50' : 
                  'opacity-60'
                }`}>
                  <div className={`h-2 bg-gradient-to-r ${stage.color}`} />
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${stage.color} text-white`}>
                          <StageIcon className="h-6 w-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <CardTitle className="text-xl">{stage.name}</CardTitle>
                            {isComplete && (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            )}
                            {isCurrent && (
                              <Badge className="bg-green-500 text-white animate-pulse">Current</Badge>
                            )}
                          </div>
                          <CardDescription className="flex items-center gap-2">
                            <Clock className="h-4 w-4" /> {stage.duration}
                          </CardDescription>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-slate-600">
                        <Award className="h-3 w-3 mr-1" /> {stage.badge}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4">{stage.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-2">
                      {stage.tasks.map((task, taskIdx) => (
                        <div 
                          key={`stage-${stage.id}-task-${taskIdx}`}
                          className={`flex items-center gap-2 p-2 rounded ${
                            isComplete ? 'text-green-700' : 'text-slate-600'
                          }`}
                        >
                          <CheckCircle className={`h-4 w-4 ${
                            isComplete ? 'text-green-500' : 'text-slate-300'
                          }`} />
                          <span className="text-sm">{task}</span>
                        </div>
                      ))}
                    </div>

                    {isCurrent && idx < stages.length - 1 && (
                      <Button 
                        className="mt-4 bg-green-600 hover:bg-green-700"
                        onClick={() => handleAdvanceStage(stages[idx + 1].id)}
                      >
                        Mark Complete & Advance <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <Link to="/workforce/employers">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="py-4 flex items-center gap-3">
                <Building2 className="h-8 w-8 text-purple-600" />
                <div>
                  <p className="font-medium text-slate-800">Employer Network</p>
                  <p className="text-sm text-slate-500">Browse opportunities</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/workforce/support">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="py-4 flex items-center gap-3">
                <Heart className="h-8 w-8 text-rose-600" />
                <div>
                  <p className="font-medium text-slate-800">Support Services</p>
                  <p className="text-sm text-slate-500">Get help with barriers</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/badges">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="py-4 flex items-center gap-3">
                <Award className="h-8 w-8 text-amber-600" />
                <div>
                  <p className="font-medium text-slate-800">PROOF Badges</p>
                  <p className="text-sm text-slate-500">View all credentials</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}
